import { useEffect, useState } from "react";
import "@/App.css";
import {
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
  Sprout,
  FlaskConical,
  Flame,
  Package,
  HandHeart,
  Leaf,
  TestTube2,
  BadgeCheck,
  Truck,
  Heart,
  Wheat,
  Droplets,
  Menu,
  X,
  ArrowRight,
  Star,
  CircleDot,
  Quote,
} from "lucide-react";

const WHATSAPP = "918800368910";
const PHONE_DISPLAY = "+91 88003 68910";
const EMAIL = "info@surakshitfoods.co.in";
const ADDRESS_LINE_1 = "Milak Lachi, Main Road, Sec G-3";
const ADDRESS_LINE_2 = "Milak Lachchhi, Greater Noida";
const ADDRESS_LINE_3 = "Gautam Buddha Nagar, Uttar Pradesh";
const FSSAI = "2272646000724";
const LOGO = "/images/logo.jpeg";

const waLink = (msg) =>
  `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`;

/* ---------- DATA ---------- */
const products = [
  {
    key: "ghee",
    name: "Bilona Ghee",
    tagline: "Hand-churned the grandmother way",
    desc:
      "Made using the ancient hand-churning Bilona method from quality-tested cow milk. Rich in vitamins A, D, E & K. No adulteration. No blending.",
    tags: ["Hand Churned", "A2 Milk", "No Blending"],
    Icon: Droplets,
    accent: "var(--sf-mustard)",
    bg: "linear-gradient(135deg, #fff3d1 0%, #f3d98a 100%)",
  },
  {
    key: "oil",
    name: "Cold Press Mustard Oil",
    tagline: "Pungent, golden, full-bodied",
    desc:
      "Extracted using traditional cold press (kacchi ghani). Preserves the full nutritional value, the natural pungency and colour. No heat, no chemicals.",
    tags: ["Cold Pressed", "Chemical Free", "Full Nutrition"],
    Icon: Sprout,
    accent: "var(--sf-terracotta)",
    bg: "linear-gradient(135deg, #fde2c1 0%, #e9a763 100%)",
  },
  {
    key: "khand",
    name: "Khand",
    tagline: "Unrefined desi sugar",
    desc:
      "Minimally processed natural sugar that retains molasses and trace minerals. A healthier everyday alternative to bleached white sugar.",
    tags: ["Unrefined", "No Bleaching", "Natural"],
    Icon: Wheat,
    accent: "#d6b66a",
    bg: "linear-gradient(135deg, #fdf3d8 0%, #ddc285 100%)",
  },
  {
    key: "gur",
    name: "Natural Gur",
    tagline: "Pure sugarcane goodness",
    desc:
      "Prepared from fresh sugarcane juice using traditional methods on wood fire. Rich in iron and minerals. No chemical treatment, no synthetic additives.",
    tags: ["Traditional", "Iron Rich", "No Chemicals"],
    Icon: Flame,
    accent: "#a3641f",
    bg: "linear-gradient(135deg, #f1c98d 0%, #b87a3a 100%)",
  },
  {
    key: "vinegar",
    name: "Indian Sugarcane Vinegar",
    tagline: "Naturally fermented sirka",
    desc:
      "Slow-fermented from pure Indian sugarcane juice — no synthetic acetic acid, no artificial colour. Tangy, aromatic and rich in natural goodness. The traditional Indian sirka, brought back honestly.",
    tags: ["Naturally Fermented", "No Synthetic Acid", "Made Slow"],
    Icon: FlaskConical,
    accent: "#7a8f4a",
    bg: "linear-gradient(135deg, #e8eccb 0%, #9aaa5e 100%)",
    isNew: true,
  },
];

const processSteps = [
  { n: 1, title: "Milk & Cane Sourcing", desc: "Directly from trusted farmers we know personally in and around our village.", Icon: HandHeart },
  { n: 2, title: "Quality Testing", desc: "Every batch tested for purity before production. Adulteration is rejected at entry.", Icon: TestTube2 },
  { n: 3, title: "Traditional Prep", desc: "Curd set overnight, sugarcane crushed fresh, mustard kept whole — the slow way.", Icon: Leaf },
  { n: 4, title: "Bilona / Kachhi Ghani / Wood Fire", desc: "Hand-churned. Cold-pressed. Slow-cooked. No shortcuts at this stage, ever.", Icon: Flame },
  { n: 5, title: "Slow Finish", desc: "Ghee golden on low flame. Vinegar fermented for weeks. Gur set in clay pans.", Icon: Sprout },
  { n: 6, title: "Packed & Delivered", desc: "Clean labelled jars with FSSAI number and batch details. Delivered fresh, pan-India.", Icon: Package },
];

const why = [
  { title: "Tested before it's made", desc: "We test inputs for adulteration before production — not after.", Icon: TestTube2 },
  { title: "True Bilona method", desc: "Actual curd → hand-churning → makhan → ghee. Not cream-separated.", Icon: Droplets },
  { title: "Zero synthetic additives", desc: "No artificial colour. No preservatives. No cheap blending oils.", Icon: Leaf },
  { title: "Known local farmers", desc: "Two steps — farmer to us, us to you. No anonymous middlemen.", Icon: HandHeart },
  { title: "FSSAI certified", desc: `Reg. No. ${FSSAI}. Every product carries batch & date.`, Icon: BadgeCheck },
  { title: "Family-first standard", desc: "Our founders feed this to their own kids. That is the real test.", Icon: Heart },
];

/* ---------- LAYOUT ---------- */

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#products", label: "Products" },
    { href: "#story", label: "Our Story" },
    { href: "#process", label: "How It's Made" },
    { href: "#why", label: "Why Surakshit" },
    { href: "#testimonials", label: "Reviews" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-[#faf4e3]/95 backdrop-blur-md shadow-[0_2px_18px_-10px_rgba(42,31,23,0.35)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between py-3">
        <a href="#home" data-testid="logo-link" className="flex items-center gap-3 group">
          <img
            src={LOGO}
            alt="Surakshit Foods"
            className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover ring-2 ring-[var(--sf-mustard)] ring-offset-2 ring-offset-[#faf4e3] transition-transform group-hover:scale-105"
          />
          <div className="leading-tight">
            <div className="font-display text-[1.25rem] md:text-[1.4rem] font-bold text-[var(--sf-forest-deep)] tracking-tight">
              Surakshit Foods
            </div>
            <div className="font-hand text-[var(--sf-terracotta)] text-base -mt-1">
              Direct From Villages
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-[var(--sf-ink)] hover:text-[var(--sf-forest)] font-medium text-[0.95rem] transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-[var(--sf-mustard)] hover:after:w-full after:transition-all"
            >
              {l.label}
            </a>
          ))}
          <a
            href={waLink("Hi, I want to order from Surakshit Foods")}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-order-btn"
            className="btn-primary !py-2.5 !px-5 text-sm"
          >
            Order on WhatsApp <ArrowRight size={16} />
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 rounded-md text-[var(--sf-forest-deep)] hover:bg-[var(--sf-cream-warm)]"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#faf4e3] border-t border-[var(--sf-cream-warm)] px-5 py-4 space-y-3" data-testid="mobile-menu">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-2 text-[var(--sf-ink)] font-medium border-b border-[var(--sf-cream-warm)]"
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href={waLink("Hi, I want to order from Surakshit Foods")}
            target="_blank"
            rel="noreferrer"
            className="btn-primary w-full justify-center"
          >
            Order on WhatsApp
          </a>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <section id="home" data-testid="hero-section" className="relative overflow-hidden">
    <div className="absolute inset-0 bg-paisley opacity-90" />
    {/* Decorative wheat sprigs */}
    <Wheat className="absolute -left-6 top-24 text-[var(--sf-mustard)] opacity-25 w-44 h-44 animate-float-slower" />
    <Sprout className="absolute right-8 bottom-12 text-[var(--sf-forest-soft)] opacity-25 w-32 h-32 animate-float-slow" />

    <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-10 pb-20 md:pt-16 md:pb-28 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7 fade-up">
        <div className="kicker mb-6" data-testid="hero-kicker">
          <span>shuddh khao, surakshit raho</span>
        </div>
        <h1 className="font-display font-bold text-[var(--sf-charcoal)] leading-[1.02] tracking-tight text-[2.6rem] sm:text-[3.4rem] lg:text-[4.4rem]">
          Food that is truly
          <br />
          <em className="not-italic relative inline-block">
            <span className="curl-underline pr-2">Surakshit</span>
          </em>{" "}
          —
          <br />
          <span className="text-[var(--sf-forest-deep)]">Pure. Safe. Honest.</span>
        </h1>
        <p className="mt-7 text-lg text-[var(--sf-ink)]/80 max-w-xl leading-relaxed">
          From tested milk to your kitchen table. <strong>Bilona Ghee</strong>, Cold Press
          Mustard Oil, Khand, Natural Gur and our new{" "}
          <span className="text-[var(--sf-terracotta-deep)] font-semibold">Indian Sugarcane Vinegar</span>{" "}
          — made without shortcuts or compromise. Direct from the village to your home.
        </p>

        <div className="mt-9 flex flex-wrap gap-4">
          <a
            href={waLink("Hi, I want to order from Surakshit Foods")}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-order-btn"
            className="btn-primary"
          >
            Order on WhatsApp <ArrowRight size={18} />
          </a>
          <a href="#products" data-testid="hero-products-btn" className="btn-outline">
            See Our 5 Products
          </a>
        </div>

        <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3 text-sm">
          {[
            { Icon: ShieldCheck, label: "FSSAI Certified" },
            { Icon: TestTube2, label: "Quality Tested" },
            { Icon: Leaf, label: "No Adulteration" },
            { Icon: Truck, label: "Pan-India Delivery" },
          ].map(({ Icon, label }) => (
            <span key={label} className="flex items-center gap-2 text-[var(--sf-forest-deep)] font-semibold">
              <Icon size={18} className="text-[var(--sf-mustard-deep)]" /> {label}
            </span>
          ))}
        </div>
      </div>

      {/* Hero visual: logo medallion */}
      <div className="lg:col-span-5 flex justify-center lg:justify-end" data-testid="hero-logo">
        <div className="relative">
          <div className="absolute -inset-8 rounded-full bg-[var(--sf-mustard)] opacity-15 blur-3xl" />
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-[var(--sf-forest)]/30 animate-spin-slow" />
          <img
            src={LOGO}
            alt="Surakshit Foods Logo"
            className="relative w-[280px] h-[280px] md:w-[400px] md:h-[400px] rounded-full object-cover shadow-[0_30px_60px_-25px_rgba(47,74,44,0.55)] ring-[10px] ring-[var(--sf-cream)] ring-offset-2 ring-offset-[var(--sf-mustard)]"
          />
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-[var(--sf-charcoal)] text-[var(--sf-mustard)] px-5 py-2 rounded-full font-hand text-xl shadow-lg whitespace-nowrap">
            Since 2024 · Greater Noida
          </div>
        </div>
      </div>
    </div>

    {/* Marquee strip */}
    <div className="marquee-strip relative">
      <div className="flex whitespace-nowrap py-3 animate-marquee">
        {Array(2).fill(0).map((_, i) => (
          <div key={i} className="flex items-center gap-12 px-6 font-display text-lg">
            <span className="flex items-center gap-3"><CircleDot size={12} /> Milk from local farmers</span>
            <span className="flex items-center gap-3"><CircleDot size={12} /> Every batch quality tested</span>
            <span className="flex items-center gap-3"><CircleDot size={12} /> Hand-churned Bilona</span>
            <span className="flex items-center gap-3"><CircleDot size={12} /> Naturally fermented vinegar</span>
            <span className="flex items-center gap-3"><CircleDot size={12} /> Zero artificial additives</span>
            <span className="flex items-center gap-3"><CircleDot size={12} /> Pan-India delivery</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Products = () => (
  <section id="products" className="relative py-24 md:py-32" data-testid="products-section">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="max-w-2xl">
        <div className="kicker mb-4">our village pantry</div>
        <h2 className="font-display font-bold text-[var(--sf-charcoal)] text-[2.3rem] md:text-[3rem] leading-tight">
          Five products. <br className="hidden md:block" />
          One honest promise.
        </h2>
        <p className="mt-5 text-[var(--sf-ink)]/75 text-lg">
          Everything we make starts with the same question — would we feed this to
          our own family? If the answer is no, it doesn&apos;t leave our shed.
        </p>
      </div>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
        {products.map((p, idx) => (
          <article
            key={p.key}
            data-testid={`product-card-${p.key}`}
            className="product-card group p-7 flex flex-col"
            style={{ animationDelay: `${idx * 80}ms` }}
          >
            {p.isNew && (
              <span
                data-testid="product-new-badge"
                className="absolute top-5 right-5 bg-[var(--sf-terracotta)] text-white text-[0.65rem] tracking-widest uppercase px-3 py-1.5 rounded-full font-bold shadow-md flex items-center gap-1.5"
              >
                <Star size={11} fill="currentColor" /> New
              </span>
            )}

            <div
              className="w-20 h-20 rounded-2xl grid place-items-center mb-6 shadow-inner"
              style={{ background: p.bg }}
            >
              <p.Icon size={36} className="text-[var(--sf-charcoal)]/85" strokeWidth={1.6} />
            </div>

            <h3 className="font-display font-bold text-2xl text-[var(--sf-forest-deep)] leading-tight">
              {p.name}
            </h3>
            <p className="font-hand text-[var(--sf-terracotta)] text-lg mt-1">
              {p.tagline}
            </p>
            <p className="text-[var(--sf-ink)]/75 leading-relaxed mt-4 text-[0.95rem] flex-1">
              {p.desc}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-5">
              {p.tags.map((t) => (
                <span key={t} className="tag">{t}</span>
              ))}
            </div>
            <a
              href={waLink(`Hi, I want to order ${p.name}`)}
              target="_blank"
              rel="noreferrer"
              data-testid={`product-order-${p.key}`}
              className="mt-6 inline-flex items-center gap-2 font-semibold text-[var(--sf-forest)] hover:text-[var(--sf-terracotta)] transition-colors group/link"
            >
              Order on WhatsApp
              <ArrowRight size={16} className="transition-transform group-hover/link:translate-x-1" />
            </a>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const Story = () => (
  <section id="story" className="relative py-24 md:py-32 bg-[var(--sf-forest-deep)] text-[var(--sf-cream-soft)] overflow-hidden" data-testid="story-section">
    <div className="absolute -top-20 -right-20 w-96 h-96 bg-[var(--sf-mustard)] opacity-10 rounded-full blur-3xl" />
    <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-[var(--sf-terracotta)] opacity-10 rounded-full blur-3xl" />

    <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <div className="kicker mb-4" style={{ color: "var(--sf-mustard)" }}>our story</div>
        <h2 className="font-display font-bold text-[2.3rem] md:text-[3rem] leading-tight">
          We started because we couldn&apos;t find food we could{" "}
          <em className="text-[var(--sf-mustard)] not-italic">trust.</em>
        </h2>
        <div className="space-y-5 mt-7 text-[var(--sf-cream-soft)]/85 text-lg leading-relaxed">
          <p>
            Like millions of Indian families, we grew up trusting ghee from the market.
            Then we started asking questions — and realised how much had changed in how
            food is made today.
          </p>
          <p>
            Surakshit Foods started in our village with one simple idea —{" "}
            <strong className="text-[var(--sf-mustard)]">go back to the old ways.</strong>{" "}
            Work directly with local farmers. Test every batch. Churn the slow way. Press cold.
            Ferment naturally. Use no shortcuts. No chemicals. No blending.
          </p>
          <p>
            The name says everything. <strong>Surakshit</strong> — protected, safe. That
            is our only promise to every family that orders from us.
          </p>
        </div>
        <a
          href="#contact"
          data-testid="story-cta"
          className="btn-mustard mt-9 inline-flex"
        >
          Talk to Us <ArrowRight size={18} />
        </a>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {[
          { n: "100%", l: "Sourced directly from trusted village farmers — no middlemen, no anonymous supply chains." },
          { n: "Zero", l: "Artificial additives, preservatives or synthetic colours across any of our 5 products." },
          { n: "Every Batch", l: "Quality tested before production begins — because your family deserves nothing less." },
          { n: "5", l: "Honest products. One single standard. From milk to vinegar to gur — same family-first promise." },
        ].map((s, i) => (
          <div
            key={i}
            className="bg-[var(--sf-cream-soft)]/8 backdrop-blur-sm border border-[var(--sf-mustard)]/25 rounded-2xl p-7 hover:border-[var(--sf-mustard)]/60 transition-colors"
            data-testid={`stat-card-${i}`}
          >
            <div className="font-display font-bold text-3xl md:text-4xl text-[var(--sf-mustard)]">
              {s.n}
            </div>
            <p className="mt-3 text-[var(--sf-cream-soft)]/80 text-[0.95rem] leading-relaxed">{s.l}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Process = () => (
  <section id="process" className="relative py-24 md:py-32 bg-[var(--sf-cream)]" data-testid="process-section">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="max-w-2xl">
        <div className="kicker mb-4">how it&apos;s made</div>
        <h2 className="font-display font-bold text-[var(--sf-charcoal)] text-[2.3rem] md:text-[3rem] leading-tight">
          The slow way. <br className="hidden md:block" /> The right way.
        </h2>
        <p className="mt-5 text-[var(--sf-ink)]/75 text-lg">
          Modern factories use cream separators, synthetic acetic acid, and chemical
          refining. We use the same methods our grandmothers used. Here&apos;s how, step by step.
        </p>
      </div>
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {processSteps.map((s) => (
          <div key={s.n} className="step-card mt-3" data-testid={`process-step-${s.n}`}>
            <div className="step-num">{s.n}</div>
            <s.Icon size={28} className="text-[var(--sf-terracotta)] mt-2" strokeWidth={1.7} />
            <h3 className="font-display font-bold text-xl text-[var(--sf-forest-deep)] mt-3">
              {s.title}
            </h3>
            <p className="text-[var(--sf-ink)]/75 mt-2 text-[0.95rem] leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Why = () => (
  <section id="why" className="relative py-24 md:py-32 bg-paisley" data-testid="why-section">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="max-w-2xl">
        <div className="kicker mb-4">why surakshit</div>
        <h2 className="font-display font-bold text-[var(--sf-charcoal)] text-[2.3rem] md:text-[3rem] leading-tight">
          The difference is in what we <em className="not-italic text-[var(--sf-terracotta)]">don&apos;t</em> do.
        </h2>
      </div>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {why.map((w, i) => (
          <div
            key={i}
            data-testid={`why-card-${i}`}
            className="bg-[var(--sf-cream-soft)] border border-[var(--sf-cream-warm)] rounded-2xl p-7 hover:border-[var(--sf-mustard)] hover:shadow-[0_18px_40px_-20px_rgba(42,31,23,0.3)] transition-all"
          >
            <div className="w-12 h-12 rounded-xl bg-[var(--sf-forest)]/10 grid place-items-center mb-4">
              <w.Icon size={24} className="text-[var(--sf-forest-deep)]" strokeWidth={1.7} />
            </div>
            <h3 className="font-display font-bold text-xl text-[var(--sf-forest-deep)]">{w.title}</h3>
            <p className="text-[var(--sf-ink)]/75 mt-2 leading-relaxed text-[0.95rem]">{w.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const testimonials = [
  {
    quote:
      "I have been using their Bilona Ghee for over six months now. The aroma when you open the jar — that is exactly how my dadi's kitchen used to smell. My kids finally eat their paratha without complaining.",
    name: "Priya Sharma",
    role: "Mother of two · Noida",
    rating: 5,
  },
  {
    quote:
      "Switched our entire household from refined oil to their cold press mustard oil. The pungency, the colour, everything is genuine. You can taste the difference in the very first sabzi.",
    name: "Rajeev Malhotra",
    role: "Homemaker · Delhi",
    rating: 5,
  },
  {
    quote:
      "Their sugarcane vinegar is a quiet revelation. I run a small home-baking setup and the flavour it adds to my chutneys and pickles is incomparable to the mass-market brands. Pure, no chemical smell.",
    name: "Anaya Kapoor",
    role: "Home chef · Gurugram",
    rating: 5,
  },
  {
    quote:
      "I ordered the Diwali gifting hamper for my office team — ghee, gur and khand. Beautiful packaging, fresh batch, and everyone messaged me asking where I bought it from. Will reorder for sure.",
    name: "Vikram Singh",
    role: "Corporate client · Greater Noida",
    rating: 5,
  },
];

const Testimonials = () => (
  <section
    id="testimonials"
    className="relative py-24 md:py-32 bg-[var(--sf-cream-soft)] overflow-hidden"
    data-testid="testimonials-section"
  >
    <Quote
      className="absolute -top-6 left-6 text-[var(--sf-mustard)] opacity-[0.10] w-64 h-64 rotate-180"
      strokeWidth={1}
    />
    <Quote
      className="absolute -bottom-10 right-6 text-[var(--sf-forest)] opacity-[0.08] w-72 h-72"
      strokeWidth={1}
    />

    <div className="relative max-w-7xl mx-auto px-5 md:px-8">
      <div className="max-w-2xl">
        <div className="kicker mb-4">kind words</div>
        <h2 className="font-display font-bold text-[var(--sf-charcoal)] text-[2.3rem] md:text-[3rem] leading-tight">
          Trusted in homes <br className="hidden md:block" />
          across India.
        </h2>
        <p className="mt-5 text-[var(--sf-ink)]/75 text-lg">
          Real notes from real families who started cooking with us — and never went back.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-6">
        {testimonials.map((t, i) => (
          <figure
            key={i}
            data-testid={`testimonial-card-${i}`}
            className="relative bg-white/80 backdrop-blur-sm border border-[var(--sf-cream-warm)] rounded-2xl p-8 hover:border-[var(--sf-mustard)] hover:shadow-[0_22px_44px_-22px_rgba(42,31,23,0.32)] transition-all group"
          >
            <Quote
              className="absolute top-5 right-6 text-[var(--sf-mustard)] opacity-30 w-10 h-10 group-hover:opacity-60 transition-opacity"
              strokeWidth={1.5}
            />
            <div className="flex items-center gap-1 mb-4">
              {Array.from({ length: t.rating }).map((_, s) => (
                <Star
                  key={s}
                  size={16}
                  className="text-[var(--sf-mustard-deep)] fill-[var(--sf-mustard)]"
                />
              ))}
            </div>
            <blockquote className="font-display text-[1.15rem] md:text-[1.25rem] leading-relaxed text-[var(--sf-ink)] italic">
              &ldquo;{t.quote}&rdquo;
            </blockquote>
            <figcaption className="mt-6 flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-full grid place-items-center font-display font-bold text-lg text-[var(--sf-cream-soft)] shrink-0"
                style={{
                  background:
                    i % 2 === 0
                      ? "linear-gradient(135deg, var(--sf-forest) 0%, var(--sf-forest-soft) 100%)"
                      : "linear-gradient(135deg, var(--sf-terracotta) 0%, var(--sf-terracotta-deep) 100%)",
                }}
              >
                {t.name
                  .split(" ")
                  .map((n) => n[0])
                  .slice(0, 2)
                  .join("")}
              </div>
              <div>
                <div className="font-semibold text-[var(--sf-forest-deep)]">{t.name}</div>
                <div className="text-sm text-[var(--sf-ink)]/60">{t.role}</div>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>

      <div className="mt-14 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-[var(--sf-ink)]/70 text-sm">
        <div className="flex items-center gap-2 font-semibold text-[var(--sf-forest-deep)]">
          <div className="flex">
            {Array.from({ length: 5 }).map((_, s) => (
              <Star
                key={s}
                size={16}
                className="text-[var(--sf-mustard-deep)] fill-[var(--sf-mustard)]"
              />
            ))}
          </div>
          <span>4.9 / 5 average from happy households</span>
        </div>
        <span className="hidden md:inline-block w-px h-5 bg-[var(--sf-cream-warm)]" />
        <span>500+ orders delivered pan-India</span>
        <span className="hidden md:inline-block w-px h-5 bg-[var(--sf-cream-warm)]" />
        <span>Loved by families since 2024</span>
      </div>
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" className="relative py-24 md:py-32 bg-[var(--sf-charcoal)] text-[var(--sf-cream-soft)] overflow-hidden" data-testid="contact-section">
    <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle, var(--sf-mustard) 1px, transparent 1px)", backgroundSize: "30px 30px" }} />

    <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-start">
      <div>
        <div className="kicker mb-4" style={{ color: "var(--sf-mustard)" }}>order now</div>
        <h2 className="font-display font-bold text-[2.3rem] md:text-[3.2rem] leading-tight">
          Ready to eat <em className="not-italic text-[var(--sf-mustard)]">Surakshit</em>?
        </h2>
        <p className="mt-5 text-[var(--sf-cream-soft)]/80 text-lg max-w-lg">
          Order directly via WhatsApp for the fastest reply. We deliver across India.
          Bulk and corporate gifting orders are warmly welcome.
        </p>

        <div className="mt-9 flex flex-wrap gap-4">
          <a
            href={waLink("Hi, I want to order from Surakshit Foods")}
            target="_blank"
            rel="noreferrer"
            data-testid="contact-whatsapp"
            className="btn-mustard"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            WhatsApp Order
          </a>
          <a href={`tel:+${WHATSAPP}`} data-testid="contact-call" className="btn-outline" style={{ color: "var(--sf-cream-soft)", borderColor: "var(--sf-cream-soft)" }}>
            <Phone size={18} /> Call to Order
          </a>
          <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="btn-outline" style={{ color: "var(--sf-cream-soft)", borderColor: "var(--sf-cream-soft)" }}>
            <Mail size={18} /> Email Us
          </a>
        </div>

        <p className="mt-10 text-sm text-[var(--sf-cream-soft)]/55 tracking-wide">
          FSSAI Reg. No: <span className="text-[var(--sf-mustard)]">{FSSAI}</span>
        </p>
      </div>

      <div className="bg-[var(--sf-cream-soft)]/[0.06] border border-[var(--sf-mustard)]/25 rounded-3xl p-8 md:p-10 backdrop-blur-sm">
        <h3 className="font-display font-bold text-2xl text-[var(--sf-mustard)] mb-6">Reach Us</h3>

        <div className="space-y-6 text-[var(--sf-cream-soft)]/90">
          <div className="flex gap-4" data-testid="contact-address">
            <MapPin className="text-[var(--sf-mustard)] shrink-0 mt-1" size={22} />
            <div>
              <div className="text-xs uppercase tracking-widest text-[var(--sf-cream-soft)]/55 mb-1">Address</div>
              <div className="text-[1.05rem] leading-relaxed">
                {ADDRESS_LINE_1}<br />
                {ADDRESS_LINE_2}<br />
                {ADDRESS_LINE_3}
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <Phone className="text-[var(--sf-mustard)] shrink-0 mt-1" size={22} />
            <div>
              <div className="text-xs uppercase tracking-widest text-[var(--sf-cream-soft)]/55 mb-1">Phone / WhatsApp</div>
              <a href={`tel:+${WHATSAPP}`} className="text-[1.05rem] hover:text-[var(--sf-mustard)] transition-colors">
                {PHONE_DISPLAY}
              </a>
            </div>
          </div>

          <div className="flex gap-4">
            <Mail className="text-[var(--sf-mustard)] shrink-0 mt-1" size={22} />
            <div>
              <div className="text-xs uppercase tracking-widest text-[var(--sf-cream-soft)]/55 mb-1">Email</div>
              <a href={`mailto:${EMAIL}`} className="text-[1.05rem] hover:text-[var(--sf-mustard)] transition-colors">
                {EMAIL}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-[var(--sf-forest-deep)] text-[var(--sf-cream-soft)]/80 py-12" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-3 gap-10">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <img src={LOGO} alt="Surakshit Foods" className="w-14 h-14 rounded-full object-cover ring-2 ring-[var(--sf-mustard)]" />
          <div>
            <div className="font-display font-bold text-xl text-[var(--sf-cream-soft)]">Surakshit Foods</div>
            <div className="font-hand text-[var(--sf-mustard)]">Direct From Villages</div>
          </div>
        </div>
        <p className="text-sm leading-relaxed text-[var(--sf-cream-soft)]/65 max-w-xs">
          Pure, traditional, family-first food — made the slow way and delivered to your kitchen, with honesty as the only ingredient we won&apos;t compromise on.
        </p>
      </div>

      <div>
        <h4 className="font-display font-bold text-[var(--sf-mustard)] text-lg mb-4">Quick Links</h4>
        <ul className="space-y-2 text-sm">
          {[
            ["#products", "Products"],
            ["#story", "Our Story"],
            ["#process", "How It\u2019s Made"],
            ["#why", "Why Surakshit"],
            ["#contact", "Order Now"],
          ].map(([href, label]) => (
            <li key={href}>
              <a href={href} className="hover:text-[var(--sf-mustard)] transition-colors">{label}</a>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-display font-bold text-[var(--sf-mustard)] text-lg mb-4">Contact</h4>
        <p className="text-sm leading-relaxed">
          {ADDRESS_LINE_1}<br />
          {ADDRESS_LINE_2}<br />
          {ADDRESS_LINE_3}
        </p>
        <p className="text-sm mt-3">
          <a href={`tel:+${WHATSAPP}`} className="block hover:text-[var(--sf-mustard)]">{PHONE_DISPLAY}</a>
          <a href={`mailto:${EMAIL}`} className="block hover:text-[var(--sf-mustard)]">{EMAIL}</a>
        </p>
      </div>
    </div>

    <div className="deco-border my-10 max-w-7xl mx-auto opacity-50" />

    <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row gap-3 justify-between items-center text-xs text-[var(--sf-cream-soft)]/55">
      <div>© {new Date().getFullYear()} Surakshit Foods. All rights reserved.</div>
      <div>FSSAI Reg. No: {FSSAI}</div>
    </div>
  </footer>
);

const FloatingWhatsApp = () => (
  <a
    href={waLink("Hi, I want to order from Surakshit Foods")}
    target="_blank"
    rel="noreferrer"
    aria-label="Chat on WhatsApp"
    data-testid="floating-whatsapp"
    className="wa-float"
  >
    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  </a>
);

function App() {
  return (
    <div className="App" data-testid="surakshit-app">
      <Nav />
      <main>
        <Hero />
        <Products />
        <Story />
        <Process />
        <Why />
        <Testimonials />
        <Contact />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
